import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Key, Eye, EyeOff, Copy, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'

interface McpCredential {
  credential_id: string
  name: string
  credential_prefix: string
  scopes_json: string
  auth_kind: string
  created_at: string
  last_used_at?: string
  expires_at?: number
  revoked_at?: number
}

interface Workspace {
  workspace_id: string
  name: string
  client_name?: string
}

const ALL_SCOPES = [
  { key: 'workspace.context', label: 'Discover workspaces and projects', description: 'Required for all other tools. Always enable this.', defaultOn: true },
  { key: 'evidence.search', label: 'Search research evidence', description: 'Search your workspace evidence and reflections.', defaultOn: true },
  { key: 'report.draft', label: 'Draft client-ready reports', description: 'Generate report skeletons from pre-reads.', defaultOn: true },
  { key: 'pre_read.run', label: 'Run evidence-backed pre-reads', description: 'Counts against your monthly pre-read budget.', defaultOn: false },
  { key: 'validation.recommend', label: 'Plan human validation', description: 'Agency Pro+ only. Returns validation plans — does not field anything.', defaultOn: false },
  { key: 'review_risk.check', label: 'Surface review-risk signals', description: 'Runs LLM-based language-pattern checks. Decision support only.', defaultOn: false },
  { key: 'evidence.read_full', label: 'Untruncated source quotes', description: 'Returns full evidence quotes rather than 300-char previews.', defaultOn: false },
]

const EXPIRY_OPTIONS = [
  { value: '', label: 'No expiry' },
  { value: '2592000', label: '30 days' },
  { value: '7776000', label: '90 days' },
  { value: '31536000', label: '1 year' },
]

export function ApiKeysPage() {
  const [searchParams] = useSearchParams()
  const fromMcp = searchParams.get('from') === 'mcp'

  const [credentials, setCredentials] = useState<McpCredential[]>([])
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [loading, setLoading] = useState(true)

  const [showModal, setShowModal] = useState(fromMcp)
  const [name, setName] = useState('')
  const [selectedWorkspace, setSelectedWorkspace] = useState('')
  const [scopes, setScopes] = useState<Record<string, boolean>>(
    Object.fromEntries(ALL_SCOPES.map(s => [s.key, s.defaultOn]))
  )
  const [expiry, setExpiry] = useState('')
  const [generating, setGenerating] = useState(false)
  const [generatedSecret, setGeneratedSecret] = useState<string | null>(null)
  const [secretCopied, setSecretCopied] = useState(false)
  const [countdown, setCountdown] = useState(60)

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

  useEffect(() => {
    fetch('/api/v1/mcp/credentials', { headers })
      .then(r => r.json() as Promise<{ credentials: McpCredential[] }>)
      .then(data => setCredentials(data.credentials ?? []))
      .catch(() => {})
    fetch('/api/v1/workspaces', { headers })
      .then(r => r.json() as Promise<{ workspaces: Workspace[] }>)
      .then(data => {
        setWorkspaces(data.workspaces ?? [])
        if (data.workspaces?.length > 0) setSelectedWorkspace(data.workspaces[0].workspace_id)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Countdown timer once secret is shown
  useEffect(() => {
    if (!generatedSecret) return
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0 }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [generatedSecret])

  async function handleGenerate() {
    if (!name.trim()) return
    setGenerating(true)
    try {
      const enabledScopes = Object.entries(scopes).filter(([, v]) => v).map(([k]) => k)
      const body: Record<string, unknown> = {
        name: name.trim(),
        workspace_id: selectedWorkspace || null,
        scopes: enabledScopes,
      }
      if (expiry) body.expires_in_seconds = parseInt(expiry, 10)

      const res = await fetch('/api/v1/mcp/credentials', {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      })
      const data = await res.json() as { secret?: string; credential?: McpCredential }
      if (data.secret) {
        setGeneratedSecret(data.secret)
        setCountdown(60)
        if (data.credential) setCredentials(prev => [data.credential!, ...prev])
      }
    } catch { /* show error state */ }
    finally { setGenerating(false) }
  }

  function copySecret() {
    if (!generatedSecret) return
    navigator.clipboard.writeText(generatedSecret).catch(() => {})
    setSecretCopied(true)
    setTimeout(() => setSecretCopied(false), 2000)
  }

  async function revokeCredential(credentialId: string) {
    await fetch(`/api/v1/mcp/credentials/${credentialId}`, { method: 'DELETE', headers })
    setCredentials(prev => prev.map(c => c.credential_id === credentialId ? { ...c, revoked_at: Date.now() } : c))
  }

  function resetModal() {
    setShowModal(false)
    setName('')
    setScopes(Object.fromEntries(ALL_SCOPES.map(s => [s.key, s.defaultOn])))
    setExpiry('')
    setGeneratedSecret(null)
    setCountdown(60)
  }

  if (loading) {
    return <div className="p-8 text-sm text-slate-400">Loading...</div>
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <div className="space-y-1">
        <h1 className="text-2xl font-medium text-deep-ink">MCP credentials</h1>
        <p className="text-sm text-slate-500">
          For installing Echosome in Claude Desktop, Cursor, Claude Code, or another MCP-speaking AI assistant.{' '}
          <a href="/integrations/mcp" className="text-echo-indigo hover:underline">See the integration docs →</a>
        </p>
      </div>

      <Button variant="primary" size="sm" onClick={() => setShowModal(true)} className="gap-2">
        <Key className="h-4 w-4" strokeWidth={1.75} />
        Generate MCP key
      </Button>

      {/* Credentials list */}
      {credentials.length > 0 && (
        <div className="space-y-3">
          {credentials.map(cred => {
            const scopeList: string[] = (() => { try { return JSON.parse(cred.scopes_json) } catch { return [] } })()
            const isRevoked = !!cred.revoked_at
            return (
              <Card key={cred.credential_id} className={cn('space-y-2', isRevoked && 'opacity-50')}>
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-deep-ink text-sm">{cred.name}</p>
                      {isRevoked && <Badge variant="warning" size="sm">Revoked</Badge>}
                    </div>
                    <p className="text-xs text-slate-400 font-mono">{cred.credential_prefix}••••••••••••••••••••</p>
                    <div className="flex flex-wrap gap-1">
                      {scopeList.map(scope => (
                        <span key={scope} className="text-xs bg-paper-warm border border-border-warm rounded px-1.5 py-0.5 text-slate-600">{scope}</span>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400">
                      Created {new Date(cred.created_at).toLocaleDateString()}
                      {cred.last_used_at ? ` · Last used ${new Date(cred.last_used_at).toLocaleDateString()}` : ' · Never used'}
                    </p>
                  </div>
                  {!isRevoked && (
                    <button
                      onClick={() => revokeCredential(cred.credential_id)}
                      className="text-xs text-red-500 hover:underline shrink-0"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {credentials.length === 0 && (
        <div className="border border-dashed border-border-gray rounded-xl p-6 text-center text-sm text-slate-400">
          No MCP keys yet. Generate one to connect your AI assistant.
        </div>
      )}

      {/* Generate modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-medium text-deep-ink">{generatedSecret ? 'Save your API key' : 'Generate MCP key'}</h2>
              <button onClick={resetModal} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            {generatedSecret ? (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                  This secret will not be shown again. Copy it now — you have {countdown}s.
                </div>
                <div className="bg-slate-900 rounded-xl p-4 flex items-center gap-3">
                  <code className="text-green-400 text-xs font-mono flex-1 break-all">{generatedSecret}</code>
                  <button onClick={copySecret} className="text-slate-400 hover:text-white shrink-0">
                    {secretCopied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-sm text-slate-600">Paste this into your Claude Desktop config or terminal as <code>ECHOSOME_API_KEY</code>.</p>
                <Button variant="primary" size="sm" className="w-full" onClick={resetModal}>Done</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-deep-ink block mb-1">Name</label>
                  <input
                    className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-echo-indigo"
                    placeholder="My Claude Desktop"
                    value={name}
                    onChange={e => setName(e.target.value)}
                  />
                </div>

                {workspaces.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-deep-ink block mb-1">Workspace</label>
                    <select
                      className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-echo-indigo bg-white"
                      value={selectedWorkspace}
                      onChange={e => setSelectedWorkspace(e.target.value)}
                    >
                      {workspaces.map(w => (
                        <option key={w.workspace_id} value={w.workspace_id}>{w.name}{w.client_name ? ` (${w.client_name})` : ''}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-deep-ink block mb-2">Scopes</label>
                  <div className="space-y-2">
                    {ALL_SCOPES.map(scope => (
                      <label key={scope.key} className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={scopes[scope.key] ?? false}
                          onChange={e => setScopes(prev => ({ ...prev, [scope.key]: e.target.checked }))}
                          className="mt-0.5"
                        />
                        <div>
                          <p className="text-sm text-deep-ink">{scope.label}</p>
                          <p className="text-xs text-slate-400">{scope.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 mt-3 text-sm text-slate-500 cursor-pointer">
                    <input
                      type="checkbox"
                      onChange={e => setScopes(Object.fromEntries(ALL_SCOPES.map(s => [s.key, e.target.checked])))}
                    />
                    Enable all scopes
                  </label>
                </div>

                <div>
                  <label className="text-sm font-medium text-deep-ink block mb-1">Expiry</label>
                  <select
                    className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-echo-indigo bg-white"
                    value={expiry}
                    onChange={e => setExpiry(e.target.value)}
                  >
                    {EXPIRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>

                <Button
                  variant="primary" size="md"
                  className={cn('w-full', (!name.trim() || generating) && 'disabled:bg-button-disabled-bg disabled:text-button-disabled-text disabled:border-button-disabled-border')}
                  onClick={handleGenerate}
                  disabled={!name.trim() || generating}
                >
                  {generating ? 'Generating...' : 'Generate'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
